import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Loader2, Upload, X, Shield, Zap } from 'lucide-react';
import { 
  SERVICENOW_ROLES, 
  SERVICENOW_MODULES, 
  SERVICENOW_MODULES_BY_PRODUCT,
  SERVICENOW_PRODUCTS,
  SECURITY_CLEARANCE_LEVELS,
  CLEARANCE_COUNTRIES
} from '../utils/servicenowData';
import { 
  COUNTRIES, 
  SERVICENOW_CERTIFICATIONS, 
  CURRENCIES 
} from '../constants/projectData';
import LanguageSelector from './LanguageSelector';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";

// Timezone options
const TIMEZONES = [
  { value: 'UTC-12', label: 'UTC-12 (Baker Island)' },
  { value: 'UTC-11', label: 'UTC-11 (Samoa)' },
  { value: 'UTC-10', label: 'UTC-10 (Hawaii)' },
  { value: 'UTC-9', label: 'UTC-9 (Alaska)' },
  { value: 'UTC-8', label: 'UTC-8 (Pacific Time)' },
  { value: 'UTC-7', label: 'UTC-7 (Mountain Time)' },
  { value: 'UTC-6', label: 'UTC-6 (Central Time)' },
  { value: 'UTC-5', label: 'UTC-5 (Eastern Time)' },
  { value: 'UTC-4', label: 'UTC-4 (Atlantic Time)' },
  { value: 'UTC-3', label: 'UTC-3 (Brazil)' },
  { value: 'UTC-2', label: 'UTC-2 (Mid-Atlantic)' },
  { value: 'UTC-1', label: 'UTC-1 (Azores)' },
  { value: 'UTC+0', label: 'UTC+0 (London, Lisbon)' },
  { value: 'UTC+1', label: 'UTC+1 (Paris, Berlin)' },
  { value: 'UTC+2', label: 'UTC+2 (Cairo, Athens)' },
  { value: 'UTC+3', label: 'UTC+3 (Moscow, Dubai)' },
  { value: 'UTC+4', label: 'UTC+4 (Abu Dhabi)' },
  { value: 'UTC+5', label: 'UTC+5 (Karachi)' },
  { value: 'UTC+5:30', label: 'UTC+5:30 (India)' },
  { value: 'UTC+6', label: 'UTC+6 (Bangladesh)' },
  { value: 'UTC+7', label: 'UTC+7 (Bangkok)' },
  { value: 'UTC+8', label: 'UTC+8 (Singapore, Hong Kong)' },
  { value: 'UTC+9', label: 'UTC+9 (Tokyo, Seoul)' },
  { value: 'UTC+10', label: 'UTC+10 (Sydney)' },
  { value: 'UTC+11', label: 'UTC+11 (Solomon Islands)' },
  { value: 'UTC+12', label: 'UTC+12 (Auckland)' }
];

const ResourceFormComplete = ({ formData, setFormData, onSubmit, onCancel, loading }) => {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [selectedOtherCerts, setSelectedOtherCerts] = useState([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [moduleSearch, setModuleSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [certSearch, setCertSearch] = useState('');
  const [otherCertSearch, setOtherCertSearch] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [showCertDropdown, setShowCertDropdown] = useState(false);
  const [showOtherCertDropdown, setShowOtherCertDropdown] = useState(false);
  const [hasEndDate, setHasEndDate] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);
  
  // Common IT certifications and tools
  const OTHER_CERTIFICATIONS = [
    // ITIL
    'ITIL 4 Foundation',
    'ITIL 4 Managing Professional',
    'ITIL 4 Strategic Leader',
    'ITIL v3 Foundation',
    'ITIL v3 Intermediate',
    'ITIL v3 Expert',
    // Project Management
    'PMP (Project Management Professional)',
    'PRINCE2 Foundation',
    'PRINCE2 Practitioner',
    'Scrum Master (CSM)',
    'Product Owner (CSPO)',
    'SAFe Agilist',
    'SAFe Practitioner',
    // Cloud & DevOps
    'AWS Solutions Architect',
    'AWS Developer',
    'AWS SysOps Administrator',
    'Azure Administrator',
    'Azure Developer',
    'Azure Solutions Architect',
    'Google Cloud Professional',
    'Kubernetes Administrator (CKA)',
    'Docker Certified Associate',
    'Terraform Associate',
    // Microsoft
    'Microsoft 365 Certified',
    'Microsoft Power Platform',
    'Microsoft Dynamics 365',
    'Microsoft SharePoint',
    // Development & Data
    'JavaScript Developer',
    'Python Developer',
    'Java Developer',
    'SQL Server Certified',
    'Oracle Database Certified',
    'MongoDB Developer',
    // Tools
    'Jira Administrator',
    'Confluence Administrator',
    'Salesforce Administrator',
    'Salesforce Developer',
    'ServiceNow Scripting',
    'Splunk Certified',
    'Datadog Fundamentals',
    // Security
    'CISSP',
    'CompTIA Security+',
    'CEH (Certified Ethical Hacker)',
    // Other
    'Six Sigma Green Belt',
    'Six Sigma Black Belt',
    'TOGAF Certified',
    'Lean IT Foundation'
  ];
  
  // Helper function to get certification acronyms
  const getCertAcronym = (cert) => {
    const acronyms = {
      'Certified System Administrator': 'CSA',
      'Certified Application Developer': 'CAD',
      'Certified Implementation Specialist': 'CIS',
      'Certified Technical Architect': 'CTA',
      'Certified Master Architect': 'CMA'
    };
    return acronyms[cert] || cert;
  };

  // Initialize hasEndDate based on formData.available_to
  useEffect(() => {
    if (formData.available_to) {
      setHasEndDate(true);
    }
  }, [formData.available_to]);
  
  // Initialize from formData ONLY on mount
  useEffect(() => {
    if (formData.roles && Array.isArray(formData.roles)) {
      setSelectedRoles(formData.roles);
    }
    if (formData.modules && Array.isArray(formData.modules)) {
      setSelectedModules(formData.modules);
    }
    if (formData.certifications && Array.isArray(formData.certifications)) {
      setSelectedCertifications(formData.certifications);
    }
    if (formData.other_certifications && Array.isArray(formData.other_certifications)) {
      setSelectedOtherCerts(formData.other_certifications);
    }
    if (formData.servicenow_products && Array.isArray(formData.servicenow_products)) {
      setSelectedProducts(formData.servicenow_products);
    }
  }, []);

  // Filter roles by search (name or acronym)
  const filteredRoles = useMemo(() => 
    SERVICENOW_ROLES.filter(role => 
      role.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
      role.acronym.toLowerCase().includes(roleSearch.toLowerCase())
    ), [roleSearch]
  );

  const handleRoleToggle = useCallback((roleName) => {
    setSelectedRoles(prev => {
      const newRoles = prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName];
      setFormData(fd => ({ ...fd, roles: newRoles }));
      return newRoles;
    });
  }, [setFormData]);

  const handleModuleToggle = useCallback((module) => {
    setSelectedModules(prev => {
      const newModules = prev.includes(module)
        ? prev.filter(m => m !== module)
        : [...prev, module];
      setFormData(fd => ({ ...fd, modules: newModules }));
      return newModules;
    });
  }, [setFormData]);

  const handleCertToggle = useCallback((cert) => {
    setSelectedCertifications(prev => {
      const newCerts = prev.includes(cert)
        ? prev.filter(c => c !== cert)
        : [...prev, cert];
      setFormData(fd => ({ ...fd, certifications: newCerts }));
      return newCerts;
    });
  }, [setFormData]);

  const handleOtherCertToggle = useCallback((cert) => {
    setSelectedOtherCerts(prev => {
      const newCerts = prev.includes(cert)
        ? prev.filter(c => c !== cert)
        : [...prev, cert];
      setFormData(fd => ({ ...fd, other_certifications: newCerts }));
      return newCerts;
    });
  }, [setFormData]);

  const addCustomOtherCert = useCallback((customCert) => {
    if (customCert && !selectedOtherCerts.includes(customCert)) {
      const newCerts = [...selectedOtherCerts, customCert];
      setSelectedOtherCerts(newCerts);
      setFormData(fd => ({ ...fd, other_certifications: newCerts }));
      setOtherCertSearch('');
    }
  }, [selectedOtherCerts, setFormData]);

  // Enhanced module search - supports product acronym search (e.g., "CSM" shows all CSM modules)
  const filteredModules = useMemo(() => {
    if (!moduleSearch.trim()) return SERVICENOW_MODULES;
    
    const searchLower = moduleSearch.toLowerCase().trim();
    const matchingModules = new Set();
    
    // Check if search matches a product acronym - if so, add all modules for that product
    for (const [productKey, modules] of Object.entries(SERVICENOW_MODULES_BY_PRODUCT)) {
      const productLower = productKey.toLowerCase();
      // Match product acronym (e.g., "CSM", "ITSM", "ITOM")
      if (productLower.includes(searchLower) || productLower.split(' ').some(part => part.includes(searchLower))) {
        modules.forEach(m => matchingModules.add(m));
      }
    }
    
    // Also filter by direct module name match
    SERVICENOW_MODULES.forEach(m => {
      if (m.toLowerCase().includes(searchLower)) {
        matchingModules.add(m);
      }
    });
    
    return Array.from(matchingModules);
  }, [moduleSearch]);

  // Enhanced certification search - supports acronym search (e.g., "CIS" shows all CIS-* certs)
  const filteredCerts = useMemo(() => {
    if (!certSearch.trim()) return SERVICENOW_CERTIFICATIONS;
    
    const searchLower = certSearch.toLowerCase().trim();
    
    return SERVICENOW_CERTIFICATIONS.filter(c => {
      const certLower = c.toLowerCase();
      
      // Direct text match
      if (certLower.includes(searchLower)) return true;
      
      // Extract acronym from certification name (e.g., "(CIS)" or "CIS-ITSM")
      const acronymMatch = c.match(/\(([A-Z-]+)\)|\b([A-Z]{2,})\b/);
      if (acronymMatch) {
        const acronym = acronymMatch[1] || acronymMatch[2];
        if (acronym && acronym.toLowerCase().includes(searchLower)) {
          return true;
        }
      }
      
      // Check common acronyms mapping
      const certAcronyms = {
        'CIS': 'Certified Implementation Specialist',
        'CAS': 'Certified Application Specialist',
        'CAD': 'Certified Application Developer',
        'CSA': 'Certified System Administrator',
        'ITSM': 'IT Service Management',
        'CSM': 'Customer Service Management',
        'HRSD': 'HR Service Delivery',
        'ITOM': 'IT Operations Management',
        'ITAM': 'IT Asset Management',
        'SPM': 'Strategic Portfolio Management',
        'SecOps': 'Security Operations',
        'GRC': 'Governance Risk and Compliance',
        'FSM': 'Field Service Management',
        'IRM': 'Integrated Risk Management'
      };
      
      for (const [acronym, full] of Object.entries(certAcronyms)) {
        if (acronym.toLowerCase().includes(searchLower) && certLower.includes(full.toLowerCase())) {
          return true;
        }
      }
      
      return false;
    });
  }, [certSearch]);

  const filteredProducts = useMemo(() => 
    SERVICENOW_PRODUCTS.filter(p => 
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.fullName.toLowerCase().includes(productSearch.toLowerCase())
    ), [productSearch]
  );

  const handleProductToggle = useCallback((productId) => {
    setSelectedProducts(prev => {
      const newProducts = prev.includes(productId)
        ? prev.filter(p => p !== productId)
        : [...prev, productId];
      setFormData(fd => ({ ...fd, servicenow_products: newProducts }));
      return newProducts;
    });
  }, [setFormData]);

  const filteredOtherCerts = useMemo(() => 
    OTHER_CERTIFICATIONS.filter(c => 
      c.toLowerCase().includes(otherCertSearch.toLowerCase()) &&
      !selectedOtherCerts.includes(c)
    ), [otherCertSearch, selectedOtherCerts]
  );

  // AI Bio generation function
  const generateAIBio = async () => {
    setGeneratingBio(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resources/generate-bio-preview`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role || selectedRoles[0],
          seniority: formData.seniority,
          it_experience_years: formData.it_experience_years,
          servicenow_experience_years: formData.servicenow_experience_years,
          experience_years: formData.servicenow_experience_years, // Backwards compatibility
          modules_mastered: selectedModules,
          certifications: selectedCertifications,
          roles: selectedRoles,
          other_certifications: selectedOtherCerts,
          work_city: formData.work_city,
          work_country: formData.work_country,
          linkedin: formData.linkedin
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate bio');
      
      const data = await response.json();
      setFormData(fd => ({ ...fd, bio: data.bio }));
      
      if (window.toast) {
        window.toast.success('Bio generated successfully!');
      }
    } catch (error) {
      console.error('Error generating bio:', error);
      if (window.toast) {
        window.toast.error('Failed to generate bio. Please try again.');
      }
    } finally {
      setGeneratingBio(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* 1. BASIC INFORMATION */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          📋 Basic Information
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">First Name *</Label>
            <Input
              id="first_name"
              required
              value={formData.first_name || ''}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="John"
            />
          </div>
          
          <div>
            <Label htmlFor="last_name">Last Name *</Label>
            <Input
              id="last_name"
              required
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Doe"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="work_city">Work City *</Label>
            <Input
              id="work_city"
              required
              value={formData.work_city || ''}
              onChange={(e) => setFormData({ ...formData, work_city: e.target.value })}
              placeholder="Paris"
            />
            <p className="text-xs text-gray-500 mt-1">City where the resource usually works from</p>
          </div>

          <div>
            <Label htmlFor="work_country">Work Country *</Label>
            <Select
              value={formData.work_country || ''}
              onValueChange={(value) => setFormData({ ...formData, work_country: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country..." />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="cv_url">CV Upload</Label>
          <div className="flex items-center gap-2">
            <Input
              id="cv_url"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setFormData({ ...formData, cv_file: file, cv_filename: file.name });
                }
              }}
              className="flex-1"
            />
          </div>
          {formData.cv_filename && (
            <p className="text-xs text-blue-600 mt-1">📎 {formData.cv_filename} - Will be uploaded on save</p>
          )}
          {formData.cv_url && (
            <p className="text-xs text-green-600 mt-1">✓ CV already uploaded</p>
          )}
        </div>

        <div>
          <Label htmlFor="linkedin_basic">LinkedIn Profile</Label>
          <Input
            id="linkedin_basic"
            type="url"
            value={formData.linkedin || ''}
            onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
            placeholder="https://linkedin.com/in/..."
          />
          <p className="text-xs text-gray-500 mt-1">Your LinkedIn profile URL</p>
        </div>
      </div>

      {/* 2. ROLES & SKILLS */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          🎯 Roles & Skills
        </h3>

        <div>
          <Label>ServiceNow Roles * (multiple selection)</Label>
          <p className="text-xs text-gray-500 mb-2">Search by role name or acronym (DEV, TC, SA, BA...)</p>
          
          <Input
            placeholder="Search roles (e.g., Developer, TC, SA, Architect...)"
            value={roleSearch}
            onChange={(e) => {
              setRoleSearch(e.target.value);
              setShowRoleDropdown(true);
            }}
            onFocus={() => setShowRoleDropdown(true)}
            className="mb-2"
          />
          
          {selectedRoles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
              {selectedRoles.map(roleName => {
                const role = SERVICENOW_ROLES.find(r => r.name === roleName);
                return (
                  <span key={roleName} className="bg-blue-100 text-blue-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium">
                    {role ? `${role.acronym} - ${role.name}` : roleName}
                    <X className="h-3 w-3 cursor-pointer hover:text-blue-600" onClick={() => handleRoleToggle(roleName)} />
                  </span>
                );
              })}
            </div>
          )}
          
          {showRoleDropdown && (
            <div className="border rounded-lg p-3 max-h-64 overflow-y-auto bg-gray-50 mb-2 shadow-sm">
              {filteredRoles.length === 0 ? (
                <div className="p-2 text-sm text-gray-500 text-center">No role found for &quot;{roleSearch}&quot;</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {filteredRoles.map((role) => (
                    <label
                      key={role.id}
                      className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                        selectedRoles.includes(role.name)
                          ? 'bg-blue-100 border-blue-300 border'
                          : 'hover:bg-white border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.name)}
                        onChange={() => handleRoleToggle(role.name)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-800">
                        <strong className="text-blue-700">{role.acronym}</strong> - {role.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowRoleDropdown(!showRoleDropdown)}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            {showRoleDropdown ? 'Hide roles list' : 'Browse all roles'}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="it_experience_years">Overall IT Experience (years) *</Label>
            <Input
              id="it_experience_years"
              type="number"
              min="0"
              max="50"
              required
              value={formData.it_experience_years || ''}
              onChange={(e) => setFormData({ ...formData, it_experience_years: e.target.value })}
              placeholder="10"
            />
            <p className="text-xs text-gray-500 mt-1">Total years in IT</p>
          </div>
          
          <div>
            <Label htmlFor="servicenow_experience_years">ServiceNow Experience (years) *</Label>
            <Input
              id="servicenow_experience_years"
              type="number"
              min="0"
              max="50"
              required
              value={formData.servicenow_experience_years || ''}
              onChange={(e) => setFormData({ ...formData, servicenow_experience_years: e.target.value })}
              placeholder="5"
            />
            <p className="text-xs text-gray-500 mt-1">Years in ServiceNow ecosystem</p>
          </div>
        </div>

        {/* ServiceNow PRODUCTS Section - Primary matching signal */}
        <div className="border-t pt-4">
          <Label className="text-base font-semibold flex items-center gap-2">
            🎯 ServiceNow Products Mastered
            <span className="text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Primary Matching</span>
          </Label>
          <p className="text-xs text-gray-500 mb-3">
            Select the high-level ServiceNow product areas you have experience with. This is the primary signal for project matching.
          </p>
          
          <Input
            placeholder="Search products (e.g., ITSM, CSM, HRSD)..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            className="mb-3"
          />
          
          {selectedProducts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedProducts.map(productId => {
                const product = SERVICENOW_PRODUCTS.find(p => p.id === productId);
                return product && (
                  <span 
                    key={productId} 
                    className="bg-purple-100 text-purple-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium"
                    title={product.fullName}
                  >
                    {product.name}
                    <X className="h-3 w-3 cursor-pointer hover:text-purple-600" onClick={() => handleProductToggle(productId)} />
                  </span>
                );
              })}
            </div>
          )}
          
          <div className="border rounded-md p-3 bg-gray-50 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {filteredProducts.map((product) => (
                <label
                  key={product.id}
                  className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                    selectedProducts.includes(product.id) 
                      ? 'bg-purple-100 border-purple-300 border' 
                      : 'hover:bg-white border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={() => handleProductToggle(product.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span>
                    <strong>{product.name}</strong>
                    <span className="text-gray-500 ml-1 text-xs">({product.fullName.split(' ').slice(0, 3).join(' ')})</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ServiceNow Modules - Modern UI */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <Label className="flex items-center gap-2 mb-3">
            ServiceNow Modules Mastered
            <span className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded-full">Multiple Selection</span>
          </Label>
          
          <Input
            placeholder="Search modules by name or product (ITSM, CSM, ITOM...)"
            value={moduleSearch}
            onChange={(e) => {
              setModuleSearch(e.target.value);
              setShowModuleDropdown(true);
            }}
            onFocus={() => setShowModuleDropdown(true)}
            className="mb-3"
          />
          
          {/* Selected modules */}
          {selectedModules.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded-lg border border-blue-100">
              {selectedModules.map(module => (
                <span key={module} className="bg-blue-100 text-blue-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium">
                  {module}
                  <X className="h-3 w-3 cursor-pointer hover:text-blue-600" onClick={() => handleModuleToggle(module)} />
                </span>
              ))}
            </div>
          )}
          
          {/* Module selection dropdown */}
          {showModuleDropdown && (
            <div className="border rounded-lg p-3 bg-white max-h-64 overflow-y-auto mb-2 shadow-sm">
              {filteredModules.length === 0 ? (
                <div className="p-2 text-sm text-gray-500 text-center">No module found for &quot;{moduleSearch}&quot;</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {filteredModules.slice(0, 30).map((module) => (
                    <label
                      key={module}
                      className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                        selectedModules.includes(module)
                          ? 'bg-blue-100 border-blue-300 border'
                          : 'hover:bg-blue-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModules.includes(module)}
                        onChange={() => handleModuleToggle(module)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-800">{module}</span>
                    </label>
                  ))}
                  {filteredModules.length > 30 && (
                    <div className="col-span-2 text-center text-xs text-gray-500 py-2">
                      +{filteredModules.length - 30} more modules - refine your search
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowModuleDropdown(!showModuleDropdown)}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            {showModuleDropdown ? 'Hide modules list' : 'Browse all modules'}
          </Button>
        </div>

        {/* ServiceNow Certifications - Modern UI */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <Label className="flex items-center gap-2 mb-3">
            ServiceNow Certifications
            <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded-full">Recommended</span>
          </Label>
          
          <Input
            placeholder="Search by name or acronym (CIS, CSA, CAD, ITSM...)"
            value={certSearch}
            onChange={(e) => {
              setCertSearch(e.target.value);
              setShowCertDropdown(true);
            }}
            onFocus={() => setShowCertDropdown(true)}
            className="mb-3"
          />
          
          {selectedCertifications.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded-lg border border-green-100">
              {selectedCertifications.map(cert => (
                <span 
                  key={cert} 
                  className="bg-green-100 text-green-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium"
                  title={cert}
                >
                  {getCertAcronym(cert)}
                  <X className="h-3 w-3 cursor-pointer hover:text-green-600" onClick={() => handleCertToggle(cert)} />
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mb-2">💡 Hover over acronyms to see full certification names</p>
          
          {showCertDropdown && (
            <div className="border rounded-lg p-3 bg-white max-h-64 overflow-y-auto mb-2 shadow-sm">
              {filteredCerts.length === 0 ? (
                <div className="p-2 text-sm text-gray-500 text-center">No certification found for &quot;{certSearch}&quot;</div>
              ) : (
                <div className="grid grid-cols-1 gap-1">
                  {filteredCerts.slice(0, 20).map((cert) => (
                    <label
                      key={cert}
                      className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                        selectedCertifications.includes(cert)
                          ? 'bg-green-100 border-green-300 border'
                          : 'hover:bg-green-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCertifications.includes(cert)}
                        onChange={() => handleCertToggle(cert)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-gray-800">{cert}</span>
                    </label>
                  ))}
                  {filteredCerts.length > 20 && (
                    <div className="text-center text-xs text-gray-500 py-2">
                      +{filteredCerts.length - 20} more certifications - refine your search
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowCertDropdown(!showCertDropdown)}
            className="border-green-300 text-green-700 hover:bg-green-100"
          >
            {showCertDropdown ? 'Hide certifications list' : 'Browse all certifications'}
          </Button>
        </div>

        {/* Other Certifications & Tools - Modern UI */}
        <div className="relative bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
          <Label htmlFor="other_certifications" className="flex items-center gap-2 mb-3">
            Other Certifications & Tools (ITIL, Azure, AWS, etc.)
            <span className="text-xs px-2 py-0.5 bg-orange-500 text-white rounded-full">Optional</span>
          </Label>
          
          {/* Selected other certifications */}
          {selectedOtherCerts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded-lg border border-orange-100">
              {selectedOtherCerts.map(cert => (
                <span key={cert} className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-800 text-xs rounded-full font-medium">
                  {cert}
                  <button
                    type="button"
                    onClick={() => handleOtherCertToggle(cert)}
                    className="ml-1 text-orange-600 hover:text-orange-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Search input */}
          <div className="relative">
            <Input
              id="other_certifications"
              value={otherCertSearch}
              onChange={(e) => {
                setOtherCertSearch(e.target.value);
                setShowOtherCertDropdown(true);
              }}
              onFocus={() => setShowOtherCertDropdown(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && otherCertSearch.trim()) {
                  e.preventDefault();
                  addCustomOtherCert(otherCertSearch.trim());
                }
              }}
              placeholder="Search or type custom certification..."
              className="pr-20"
            />
            {otherCertSearch && (
              <button
                type="button"
                onClick={() => addCustomOtherCert(otherCertSearch.trim())}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
              >
                + Add
              </button>
            )}
          </div>
          
          {/* Dropdown */}
          {showOtherCertDropdown && otherCertSearch && filteredOtherCerts.length > 0 && (
            <div className="absolute z-50 left-4 right-4 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredOtherCerts.slice(0, 10).map(cert => (
                <button
                  key={cert}
                  type="button"
                  onClick={() => {
                    handleOtherCertToggle(cert);
                    setOtherCertSearch('');
                    setShowOtherCertDropdown(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-orange-50 border-b border-gray-100 last:border-0"
                >
                  {cert}
                </button>
              ))}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">💡 Type to search suggestions or add custom certifications (press Enter or click Add)</p>
        </div>

        {/* Languages with Proficiency Level - Moved here for AI Bio generation */}
        <div>
          <Label>Languages with Proficiency Level</Label>
          <LanguageSelector
            languages={formData.languages_required || []}
            onChange={(newLanguages) => setFormData({ ...formData, languages_required: newLanguages })}
          />
          <p className="text-xs text-gray-500 mt-1">💡 Add languages with CECRL proficiency level (A1-C2)</p>
        </div>
      </div>

      {/* 3. MOBILITY & REMOTE */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          🌍 Mobility & Remote Work
        </h3>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="can_work_remote"
            checked={formData.can_work_remote || false}
            onCheckedChange={(checked) => setFormData({ ...formData, can_work_remote: checked })}
          />
          <label htmlFor="can_work_remote" className="text-sm font-medium cursor-pointer">
            Can work on full remote projects
          </label>
        </div>

        {/* Timezone Availability - shown only when remote work is enabled */}
        {formData.can_work_remote && (
          <div className="ml-6 space-y-3 border-l-2 border-emerald-200 pl-4 bg-emerald-50/50 p-4 rounded-r-lg">
            <div>
              <Label className="text-emerald-800 font-semibold">🌐 Flexible Timezone Availability</Label>
              <p className="text-xs text-gray-600 mt-1 mb-3">
                Expand your resource's reach! A consultant based in Paris can work with clients in Dubai (+3h) or New York (-6h) by adapting their schedule. 
                This removes geographic limitations and opens doors to international projects.
              </p>
              <p className="text-xs text-emerald-700 font-medium mb-3">
                Select the timezone range this resource is comfortable working in:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="timezone_min" className="text-sm">From Timezone</Label>
                  <Select
                    value={formData.timezone_min || ''}
                    onValueChange={(value) => setFormData({ ...formData, timezone_min: value })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select earliest timezone..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timezone_max" className="text-sm">To Timezone</Label>
                  <Select
                    value={formData.timezone_max || ''}
                    onValueChange={(value) => setFormData({ ...formData, timezone_max: value })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select latest timezone..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Checkbox
            id="can_travel"
            checked={formData.can_travel || false}
            onCheckedChange={(checked) => setFormData({ ...formData, can_travel: checked })}
          />
          <label htmlFor="can_travel" className="text-sm font-medium cursor-pointer">
            Can travel to client sites
          </label>
        </div>

        {formData.can_travel && (
          <div className="ml-6 space-y-3 border-l-2 border-gray-200 pl-4">
            <div>
              <Label htmlFor="max_travel_distance">Maximum Travel Distance</Label>
              <Select
                value={formData.max_travel_distance || ''}
                onValueChange={(value) => setFormData({ ...formData, max_travel_distance: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50 km</SelectItem>
                  <SelectItem value="100">100 km</SelectItem>
                  <SelectItem value="200">200 km</SelectItem>
                  <SelectItem value="500">500 km</SelectItem>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="international">International</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="max_onsite_days">Max Days On-Site per Week</Label>
              <Select
                value={formData.max_onsite_days?.toString() || ''}
                onValueChange={(value) => setFormData({ ...formData, max_onsite_days: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="4">4 days</SelectItem>
                  <SelectItem value="5">5 days (full on-site possible)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* 3b. AI-AUGMENTED CONSULTANT */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          🤖 AI-Augmented Consultant
        </h3>
        <p className="text-sm text-gray-600">
          Does this consultant use AI agents to enhance their productivity? 
          AI-augmented consultants deliver more value in less time.
        </p>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_ai_augmented"
            checked={formData.is_ai_augmented || false}
            onCheckedChange={(checked) => {
              setFormData({ 
                ...formData, 
                is_ai_augmented: checked,
                ai_agents: checked ? formData.ai_agents : [] 
              });
            }}
          />
          <label htmlFor="is_ai_augmented" className="text-sm font-medium cursor-pointer">
            This consultant uses AI agents to boost productivity
          </label>
        </div>

        {formData.is_ai_augmented && (
          <div className="ml-6 space-y-3 border-l-2 border-purple-200 pl-4 bg-purple-50/50 py-3 rounded-r-md">
            <div>
              <Label htmlFor="ai_agents">AI Agents / Tools Used</Label>
              <p className="text-xs text-gray-500 mb-2">
                List the AI agents or tools this consultant uses (e.g., Cursor, GitHub Copilot, Custom GPTs, Claude, ServiceNow Now Assist, etc.)
              </p>
              <div className="flex gap-2 mb-2">
                <Input
                  id="ai_agent_input"
                  placeholder="Type an AI agent name and press Enter..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      e.preventDefault();
                      const newAgent = e.target.value.trim();
                      const currentAgents = formData.ai_agents || [];
                      if (!currentAgents.includes(newAgent)) {
                        setFormData({ 
                          ...formData, 
                          ai_agents: [...currentAgents, newAgent] 
                        });
                      }
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const input = document.getElementById('ai_agent_input');
                    if (input && input.value.trim()) {
                      const newAgent = input.value.trim();
                      const currentAgents = formData.ai_agents || [];
                      if (!currentAgents.includes(newAgent)) {
                        setFormData({ 
                          ...formData, 
                          ai_agents: [...currentAgents, newAgent] 
                        });
                      }
                      input.value = '';
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              
              {/* AI Agent Tags */}
              {formData.ai_agents && formData.ai_agents.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.ai_agents.map((agent, idx) => (
                    <span 
                      key={idx} 
                      className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                    >
                      {agent}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            ai_agents: formData.ai_agents.filter((_, i) => i !== idx)
                          });
                        }}
                        className="ml-1 hover:text-purple-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              {/* Quick suggestions */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Quick add:</p>
                <div className="flex flex-wrap gap-1">
                  {['GitHub Copilot', 'Cursor', 'Claude', 'ChatGPT/GPT-4', 'ServiceNow Now Assist', 'Custom AI Agent'].map(suggestion => (
                    <button
                      key={suggestion}
                      type="button"
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        (formData.ai_agents || []).includes(suggestion)
                          ? 'bg-purple-200 border-purple-400 text-purple-800'
                          : 'bg-white border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                      }`}
                      onClick={() => {
                        const currentAgents = formData.ai_agents || [];
                        if (currentAgents.includes(suggestion)) {
                          setFormData({
                            ...formData,
                            ai_agents: currentAgents.filter(a => a !== suggestion)
                          });
                        } else {
                          setFormData({
                            ...formData,
                            ai_agents: [...currentAgents, suggestion]
                          });
                        }
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3c. SECURITY & COMPLIANCE */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          🔐 Security & Compliance
        </h3>
        <p className="text-sm text-gray-600">
          Some projects require specific security clearances. Fill this section if applicable.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="security_clearance">Security Clearance Level</Label>
            <Select
              value={formData.security_clearance || 'none'}
              onValueChange={(value) => setFormData({ ...formData, security_clearance: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select clearance level" />
              </SelectTrigger>
              <SelectContent>
                {SECURITY_CLEARANCE_LEVELS.map(level => (
                  <SelectItem key={level.id} value={level.id}>
                    <span className="flex items-center gap-2">
                      {level.id === 'none' && <span className="text-gray-400">○</span>}
                      {level.id === 'standard' && <Shield className="w-3 h-3 text-blue-500" />}
                      {level.id === 'enhanced' && <Shield className="w-3 h-3 text-orange-500" />}
                      {level.id === 'high' && <Shield className="w-3 h-3 text-red-500" />}
                      {level.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">e.g., None, Standard, Enhanced (SÜ2, SC), High-Level (SÜ3, DV, TS)</p>
          </div>

          {formData.security_clearance && formData.security_clearance !== 'none' && (
            <div>
              <Label htmlFor="clearance_country">Clearance Valid In</Label>
              <Select
                value={formData.clearance_country || ''}
                onValueChange={(value) => setFormData({ ...formData, clearance_country: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {CLEARANCE_COUNTRIES.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="nationality">Nationality</Label>
            <Select
              value={formData.nationality || ''}
              onValueChange={(value) => setFormData({ ...formData, nationality: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select nationality (optional)" />
              </SelectTrigger>
              <SelectContent>
                {CLEARANCE_COUNTRIES.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">Some projects may have nationality requirements</p>
          </div>

          <div>
            <Label htmlFor="residence_country">Country of Residence</Label>
            <Select
              value={formData.residence_country || ''}
              onValueChange={(value) => setFormData({ ...formData, residence_country: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select residence country" />
              </SelectTrigger>
              <SelectContent>
                {CLEARANCE_COUNTRIES.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* 4. AVAILABILITY */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          📅 Availability
        </h3>

        <div>
          <Label htmlFor="availability_status">Current Availability Status *</Label>
          <Select
            value={formData.availability_status || 'available'}
            onValueChange={(value) => {
              const newFormData = { ...formData, availability_status: value };
              // Auto-fill "Available From" with today's date if "Available Now" is selected
              if (value === 'available' && !formData.available_from) {
                newFormData.available_from = new Date().toISOString().split('T')[0];
              }
              // Auto-deactivate resource when set to "Not Available"
              if (value === 'not_available') {
                newFormData.is_activated = false;
              }
              setFormData(newFormData);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available Now</SelectItem>
              <SelectItem value="available_soon">Available Soon</SelectItem>
              <SelectItem value="partially_available">Partially Available</SelectItem>
              <SelectItem value="not_available">Not Available</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            💡 &quot;Available Now&quot; auto-fills today&apos;s date • &quot;Not Available&quot; automatically deactivates the resource
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="available_from">Available From</Label>
            <Input
              id="available_from"
              type="date"
              value={formData.available_from || ''}
              onChange={(e) => setFormData({ ...formData, available_from: e.target.value })}
            />
          </div>
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                id="has_end_date"
                checked={hasEndDate}
                onChange={(e) => {
                  setHasEndDate(e.target.checked);
                  if (!e.target.checked) {
                    setFormData({ ...formData, available_to: '' });
                  }
                }}
                className="rounded border-gray-300"
              />
              <Label htmlFor="has_end_date" className="cursor-pointer font-normal">
                Set an end date for availability
              </Label>
            </div>
            {hasEndDate && (
              <>
                <Label htmlFor="available_to">Available Until</Label>
                <Input
                  id="available_to"
                  type="date"
                  value={formData.available_to || ''}
                  onChange={(e) => setFormData({ ...formData, available_to: e.target.value })}
                  min={formData.available_from || ''}
                />
              </>
            )}
            {!hasEndDate && (
              <p className="text-xs text-gray-500 mt-1">✓ Open-ended availability</p>
            )}
          </div>
          <div>
            <Label htmlFor="commitment_capacity">Commitment Capacity (%)</Label>
            <Select
              value={formData.commitment_capacity?.toString() || ''}
              onValueChange={(value) => setFormData({ ...formData, commitment_capacity: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20%</SelectItem>
                <SelectItem value="40">40%</SelectItem>
                <SelectItem value="60">60%</SelectItem>
                <SelectItem value="80">80%</SelectItem>
                <SelectItem value="100">100%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* 5. PRICING */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          💰 Pricing
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="currency">Currency *</Label>
            <Select
              value={formData.currency || 'EUR'}
              onValueChange={(value) => setFormData({ ...formData, currency: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(curr => (
                  <SelectItem key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="daily_rate">Daily Rate *</Label>
            <Input
              id="daily_rate"
              type="number"
              min="0"
              step="50"
              required
              value={formData.daily_rate || ''}
              onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
              placeholder="600"
            />
          </div>

          <div>
            <Label htmlFor="long_term_discount">Long-term Discount (&ge; 61 days) %</Label>
            <Input
              id="long_term_discount"
              type="number"
              min="0"
              max="50"
              step="5"
              value={formData.long_term_discount || ''}
              onChange={(e) => setFormData({ ...formData, long_term_discount: e.target.value })}
              placeholder="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              Discount for missions ≥ 61 days
            </p>
          </div>
        </div>

        {formData.daily_rate && formData.long_term_discount && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
            <p className="font-semibold text-blue-900">Pricing Preview:</p>
            <p className="text-blue-800">
              • Short missions (&lt; 61 days): <strong>{formData.daily_rate}{CURRENCIES.find(c => c.code === (formData.currency || 'EUR'))?.symbol}/day</strong>
            </p>
            <p className="text-blue-800">
              • Long missions (≥ 61 days): <strong>{(formData.daily_rate * (1 - formData.long_term_discount / 100)).toFixed(0)}{CURRENCIES.find(c => c.code === (formData.currency || 'EUR'))?.symbol}/day</strong> (-{formData.long_term_discount}%)
            </p>
          </div>
        )}
      </div>

      {/* 6. ADDITIONAL INFO */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          ℹ️ Additional Information
        </h3>

        <div>
          <Label htmlFor="bio">Bio / Description</Label>
          <Textarea
            id="bio"
            rows={4}
            value={formData.bio || ''}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            placeholder="Brief description of the profile and experience..."
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={generateAIBio}
            disabled={generatingBio}
          >
            {generatingBio ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              '✨ Generate Bio with AI'
            )}
          </Button>
          <p className="text-xs text-gray-500 mt-1">💡 AI will generate a professional bio based on all information provided (skills, experience, LinkedIn, certifications, etc.)</p>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Resource'
          )}
        </Button>
      </div>
    </form>
  );
};

export default ResourceFormComplete;
